import { useCallback, useMemo } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import { SignupResponse } from "../types/signup";
import { LoginResponse } from "../types/auth-types";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.detail || error?.response?.data?.message || fallback;

export const useSignupApi = () => {
  const { post } = useHttpAdapter();

  const signup = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await post<SignupResponse>("/identity/signup", {
          email,
          password,
        });

        return response.status === 200 ? response.data : {};
      } catch (e: any) {
        return {
          status: "error",
          message: getErrorMessage(e, "Signup failed"),
        };
      }
    },
    [post]
  );

  const verifyOtp = useCallback(
    async (email: string, otp: string): Promise<LoginResponse> => {
      try {
        const response = await post<LoginResponse>("/identity/verifyOtp", {
          email,
          otp,
        });

        return response.data;
      } catch (e: any) {
        return {
          status: "error",
          user: { id: "", email: "" },
          message: getErrorMessage(e, "OTP verification failed"),
        } as unknown as LoginResponse;
      }
    },
    [post]
  );

  const resendOtp = useCallback(
    async (email: string) => {
      try {
        const response = await post<{ status?: string; message?: string }>(
          "/identity/resendOtp",
          { email }
        );
        return { ok: response.status === 200, message: response.data?.message };
      } catch (e: any) {
        return {
          ok: false,
          message: getErrorMessage(e, "Failed to resend code."),
        };
      }
    },
    [post]
  );

  return useMemo(
    () => ({
      signup,
      verifyOtp,
      resendOtp,
    }),
    [signup, verifyOtp, resendOtp]
  );
};
